import { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Wallet, QrCode, ShieldAlert, Sparkles } from "lucide-react";
import { WalletTransactions } from "../../components/WalletTransactions";

export default function StudentWalletPage() {
  const { t } = useI18n();
  const [w, setW] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/canteen/wallet/student/me")
      .then((r) => setW(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Error"));
  }, []);

  if (err) {
    return (
      <Card className="p-8 text-center max-w-md mx-auto border" data-testid="student-wallet-error">
        <ShieldAlert className="w-10 h-10 mx-auto text-destructive mb-2" />
        <p className="text-sm text-muted-foreground">{err}</p>
      </Card>
    );
  }

  if (!w) {
    return <div className="text-center py-12 text-muted-foreground">{t("loading")}</div>;
  }

  const used = (w.limits.daily_limit || 0) - (w.remaining_today || 0);
  const pct = w.limits.daily_limit ? Math.min(100, (used / w.limits.daily_limit) * 100) : 0;

  return (
    <div className="max-w-xl mx-auto space-y-4" data-testid="student-wallet-page">
      <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
        <Wallet className="w-7 h-7 text-primary" /> {t("my_wallet")}
      </h1>

      <Card className="p-6 border shadow-none">
        <div className="text-center mb-5">
          <div className="text-sm text-muted-foreground">{w.student_name}</div>
          <div className="text-4xl font-bold mt-1 font-mono" data-testid="student-balance">
            {w.balance.toFixed(2)} <span className="text-base text-muted-foreground">{t("sar_unit")}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">{t("wallet_balance")}</div>
        </div>

        {/* Daily limit progress */}
        {w.limits.daily_limit > 0 && (
          <div className="mb-5">
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>{t("daily_limit")}: {w.limits.daily_limit.toFixed(2)} {t("sar_unit")}</span>
              <span>{t("remaining")}: {(w.remaining_today || 0).toFixed(2)}</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all"
                   style={{ width: `${100 - pct}%` }} />
            </div>
          </div>
        )}

        {/* QR Code */}
        <div className="flex flex-col items-center gap-3 mt-4">
          <div className="bg-white p-4 rounded-lg border-2 border-dashed border-primary/30" data-testid="student-qr">
            <QRCode value={w.qr_token} size={180} bgColor="#ffffff" fgColor="#000000" />
          </div>
          <div className="text-center">
            <p className="font-semibold text-sm flex items-center justify-center gap-1.5">
              <QrCode className="w-4 h-4 text-primary" /> {t("student_qr_title")}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{t("student_qr_hint")}</p>
            <p className="font-mono text-[10px] text-muted-foreground/70 mt-2 tracking-wider">{w.qr_token}</p>
          </div>
        </div>
      </Card>

      {/* Tips card */}
      <Card className="p-4 border shadow-none bg-amber-50/40 dark:bg-amber-950/10">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-muted-foreground space-y-1">
            <p>{t("student_qr_tip_1")}</p>
            <p>{t("student_qr_tip_2")}</p>
          </div>
        </div>
      </Card>

      {/* Recent transactions for the student */}
      <WalletTransactions filterStudentId={w.student_id} showReconcile={false} />
    </div>
  );
}
